import { getUserInfo } from '@/modules/Auth/services'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { ErrorResponse } from '@/types/shared'
import authSdk from '@/utils/sdk'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { paths } from '@/utils/path'
import { Button } from 'vnetwork-ui'
import VersionInfo from '../VersionInfo'
import { useTranslation } from 'react-i18next'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const Callback = () => {
    const [searchParams] = useSearchParams()
    const { t } = useTranslation()

    const { setEmployee } = useAuthStore()
    const {
        setPermissions,
        setIsAdmin,
        setCanFilterCustomerEventFollowers,
        setCanDeleteCustomerJourneyNote,
        setCanDeletePlainNotesOnlyCustomerJourney,
        setCanViewLeadOriginCustomers,
    } = usePermissionsStore()

    const handleGetToken = async (code: string) => {
        await authSdk.loginByCode(code)

        const { result } = await getUserInfo()
        setEmployee(result?.employee)
        setPermissions(result?.authorization?.permissions)
        setIsAdmin(result?.is_admin ?? false)
        setCanFilterCustomerEventFollowers(
            result?.can_filter_customer_event_followers ?? false
        )
        setCanDeleteCustomerJourneyNote(
            result?.can_delete_customer_journey_note ?? false
        )
        setCanDeletePlainNotesOnlyCustomerJourney(
            result?.can_delete_plain_notes_only_customer_journey ?? false
        )
        setCanViewLeadOriginCustomers(
            result?.can_view_lead_origin_customers ?? false
        )
    }

    const { mutateAsync, isPending, isError, error } = useMutationWithHandler(
        {
            mutationFn: handleGetToken,
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            showSuccessToast: false,
            showErrorToast: false,
        }
    )

    const errorMessage =
        (error as AxiosError<ErrorResponse>)?.response?.data
            ?.error_description ||
        error?.message ||
        searchParams.get('error_description') ||
        'Something went wrong'

    const isErrorPage = isError || searchParams.get('error')

    useEffect(() => {
        const code = searchParams.get('code')

        if (!code) return
        mutateAsync(code)
    }, [])

    return (
        <div
            className="w-full"
            style={{
                fontFamily: 'IBM Plex Sans, Helvetica, Arial, sans-serif',
            }}
        >
            {/* Carbon Status Header */}
            <div className="mb-6 md:mb-8">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-black leading-tight mb-2">
                    {isPending && t('authenticating')}
                    {isErrorPage && t('authentication-failed')}
                    {!isPending &&
                        !isErrorPage &&
                        t('authentication-successful')}
                </h1>
                <p className="text-sm md:text-base text-gray-600">
                    {isPending && t('verify-credentials-wait')}
                    {isErrorPage && t('problem-signing-in')}
                    {!isPending && !isErrorPage && t('welcome-datacentral')}
                </p>
            </div>

            {/* Carbon Status Display */}
            <div className="space-y-4 md:space-y-6">
                {/* Status Card */}
                <div className="bg-white border border-gray-300 p-4 sm:p-6">
                    <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className="flex-shrink-0">
                            {isPending && (
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 border border-gray-300 flex items-center justify-center">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-red-600 border-t-transparent animate-spin rounded-full"></div>
                                </div>
                            )}
                            {isErrorPage && (
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-50 border border-red-200 flex items-center justify-center">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex items-center justify-center">
                                        ⚠
                                    </div>
                                </div>
                            )}
                            {!isPending && !isErrorPage && (
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 border border-gray-300 flex items-center justify-center">
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex items-center justify-center">
                                        ✓
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-medium text-black mb-2">
                                {t('vnetwork-id-authentication')}
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                {isPending &&
                                    t('validating-credentials-session')}
                                {isErrorPage &&
                                    t('unable-authenticate-vnetwork')}
                                {!isPending &&
                                    !isErrorPage &&
                                    t('successfully-authenticated-dashboard')}
                            </p>

                            {/* Progress Indicator */}
                            {isPending && (
                                <div className="mt-3 sm:mt-4">
                                    <div className="flex justify-between text-xs text-gray-500 mb-2">
                                        <span>{t('progress')}</span>
                                        <span className="hidden sm:inline">
                                            {t('authenticating')}...
                                        </span>
                                    </div>
                                    <div className="w-full h-1 bg-gray-200 rounded-full">
                                        <div
                                            className="h-1 bg-red-600 rounded-full animate-pulse"
                                            style={{ width: '60%' }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Error Details */}
                {isErrorPage && (
                    <div className="bg-red-50 border border-red-200 p-3 sm:p-4">
                        <div className="flex items-start space-x-2 sm:space-x-3">
                            <div className="w-4 h-4 bg-red-600 rounded-full mt-0.5 flex items-center justify-center flex-shrink-0">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-red-800 mb-2">
                                    {t('error-details')}
                                </p>
                                <p className="text-sm text-red-700 mb-3 sm:mb-4 break-words">
                                    {errorMessage}
                                </p>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    className="bg-red-600 hover:bg-red-700 text-white font-medium border-0 h-9 sm:h-10 px-3 sm:px-4 text-sm"
                                >
                                    <Link
                                        to={paths.login}
                                        className="inline-flex items-center"
                                    >
                                        ← {t('try-again')}
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Actions */}
                {!isPending && !isErrorPage && (
                    <div className="bg-gray-100 border border-gray-300 p-3 sm:p-4">
                        <div className="flex items-start space-x-2 sm:space-x-3">
                            <div className="w-4 h-4 bg-red-600 rounded-full mt-0.5 flex items-center justify-center flex-shrink-0">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-black mb-2">
                                    {t('authentication-complete')}
                                </p>
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    {t('redirect-dashboard-message')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Version Info */}
                <VersionInfo align="left" />
            </div>
        </div>
    )
}

export default Callback
